import React, { useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useAuth } from '../context/AuthContext';

const SwipeableJobCard = ({ children, job, onContact, onSave, onDelete }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isRevealed, setIsRevealed] = useState(null); // 'left' eller 'right'
  const { user } = useAuth();
  const cardRef = useRef(null);
  
  const isOwnJob = user && job?.poster_id === user.id;
  const SWIPE_THRESHOLD = 80;
  const MAX_SWIPE = 160;

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      const { deltaX } = eventData;
      
      // Begrens sveipe-avstand
      let newOffset = deltaX;
      if (deltaX > 0) {
        // Sveip høyre - vis slett (kun for egne jobber)
        newOffset = isOwnJob ? Math.min(deltaX, MAX_SWIPE) : 0;
      } else {
        // Sveip venstre - vis kontakt/lagre
        newOffset = Math.max(deltaX, -MAX_SWIPE);
      }
      
      setSwipeOffset(newOffset);
    },
    onSwipedLeft: (eventData) => {
      if (Math.abs(eventData.deltaX) > SWIPE_THRESHOLD) {
        setSwipeOffset(-MAX_SWIPE);
        setIsRevealed('left');
      } else {
        resetSwipe();
      }
    },
    onSwipedRight: (eventData) => {
      if (isOwnJob && eventData.deltaX > SWIPE_THRESHOLD) {
        setSwipeOffset(MAX_SWIPE);
        setIsRevealed('right');
      } else {
        resetSwipe();
      }
    },
    onTap: () => {
      if (isRevealed) {
        resetSwipe();
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
  });

  const resetSwipe = () => {
    setSwipeOffset(0);
    setIsRevealed(null);
  };

  const handleAction = (action) => {
    action?.();
    resetSwipe();
  };

  return (
    <div className="relative overflow-hidden md:overflow-visible">
      {/* Bakgrunns-handlinger - venstre side (kontakt/lagre) */}
      <div 
        className="absolute inset-y-0 right-0 flex items-stretch md:hidden"
        style={{ width: MAX_SWIPE }}
      >
        <button
          onClick={() => handleAction(onContact)}
          className="flex-1 bg-[#1E3A5F] flex flex-col items-center justify-center text-white"
        >
          <span className="material-symbols-outlined text-2xl mb-1">chat</span>
          <span className="text-xs font-medium">Kontakt</span>
        </button>
        <button
          onClick={() => handleAction(onSave)}
          className="flex-1 bg-[#3B82F6] flex flex-col items-center justify-center text-white"
        >
          <span className="material-symbols-outlined text-2xl mb-1">bookmark</span>
          <span className="text-xs font-medium">Lagre</span>
        </button>
      </div>

      {/* Bakgrunns-handlinger - høyre side (slett) */}
      {isOwnJob && (
        <div 
          className="absolute inset-y-0 left-0 flex items-stretch md:hidden"
          style={{ width: MAX_SWIPE }}
        >
          <button
            onClick={() => handleAction(onDelete)}
            className="flex-1 bg-red-500 flex flex-col items-center justify-center text-white"
          >
            <span className="material-symbols-outlined text-2xl mb-1">delete</span>
            <span className="text-xs font-medium">Slett</span>
          </button>
        </div>
      )}

      {/* Hovedkort-innhold */}
      <div
        {...handlers}
        ref={cardRef}
        className="relative bg-white transition-transform duration-200 ease-out md:transform-none"
        style={{
          transform: `translateX(${swipeOffset}px)`,
        }}
      >
        {children}
        
        {/* Sveipe-indikator på mobil */}
        {!isRevealed && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 md:hidden opacity-30 pointer-events-none">
            <span className="material-symbols-outlined text-slate-400 text-sm">
              chevron_left
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwipeableJobCard;
